<template>
    <!-- ── text / number / date ─────────────────────────────────────────────── -->
    <v-text-field v-if="isTextField" :model-value="modelValue" :label="campo.etichetta" :type="inputType" :rules="rules"
        hide-details="auto" density="compact" variant="outlined"
        @update:model-value="$emit('update:modelValue', $event)">
        <template #prepend-inner v-if="iconaCampo">
            <v-icon size="16" class="text-medium-emphasis">{{ iconaCampo }}</v-icon>
        </template>
    </v-text-field>

    <!-- ── textarea ─────────────────────────────────────────────────────────── -->
    <v-textarea v-else-if="campo.tipoInput === 'textarea'" :model-value="modelValue" :label="campo.etichetta"
        :rules="rules" hide-details="auto" density="compact" variant="outlined" :rows="2" autoGrow :max-rows="6"
        @update:model-value="$emit('update:modelValue', $event)" />

    <!-- ── select ───────────────────────────────────────────────────────────── -->
    <template v-else-if="campo.tipoInput === 'select'">
        <!-- Verniciatura: campo testo + menu dropdown (comportamento speciale) -->
        <template v-if="campo.campoKey === 'Verniciatura'">
            <v-row dense>
                <v-col cols="12" sm="8">
                    <v-text-field :model-value="modelValue" :label="campo.etichetta" density="compact"
                        variant="outlined" hide-details @update:model-value="$emit('update:modelValue', $event)" />
                </v-col>
                <v-col cols="12" sm="4">
                    <v-menu>
                        <template #activator="{ props }">
                            <v-btn v-bind="props" block variant="outlined" prepend-icon="mdi-menu-down"
                                density="compact">
                                Elenco
                            </v-btn>
                        </template>
                        <v-list>
                            <v-list-item v-for="item in opzioniCampo" :key="item.value"
                                @click="$emit('update:modelValue', item.value)">
                                <v-list-item-title>{{ item.label }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </v-col>
            </v-row>
        </template>

        <!-- Select standard -->
        <v-select v-else :model-value="modelValue" :items="opzioniCampo" item-value="value" item-title="label"
            :label="campo.etichetta" :rules="rules" hide-details="auto" density="compact" variant="outlined"
            @update:model-value="$emit('update:modelValue', $event)" />
    </template>

    <!-- ── autocomplete ─────────────────────────────────────────────────────── -->
    <v-autocomplete v-else-if="campo.tipoInput === 'autocomplete'" :model-value="modelValue" :items="opzioniCampo"
        item-value="value" item-title="label" :label="campo.etichetta" :rules="rules" hide-details="auto"
        density="compact" variant="outlined" @update:model-value="$emit('update:modelValue', $event)" />

    <!-- ── checkbox singolo ─────────────────────────────────────────────────── -->
    <v-checkbox v-else-if="campo.tipoInput === 'checkbox'" :model-value="modelValue" :label="campo.etichetta"
        hide-details density="compact" color="primary" class="mt-0"
        @update:model-value="$emit('update:modelValue', $event)" />

    <!-- ── radio group ──────────────────────────────────────────────────────── -->
    <div v-else-if="campo.tipoInput === 'radio'">
        <div class="text-caption text-medium-emphasis mb-1">{{ campo.etichetta }}</div>
        <v-radio-group :model-value="modelValue" hide-details density="compact" inline
            @update:model-value="$emit('update:modelValue', $event)">
            <v-radio v-for="opt in opzioniCampo" :key="opt.value" :label="opt.label" :value="opt.value"
                color="primary" />
        </v-radio-group>
    </div>

    <!-- ── checkbox group ───────────────────────────────────────────────────── -->
    <div v-else-if="campo.tipoInput === 'checkboxgroup'">
        <div class="text-caption text-medium-emphasis mb-1">{{ campo.etichetta }}</div>
        <div class="d-flex flex-wrap gap-2">
            <v-checkbox v-for="opt in opzioniCampo" :key="opt.value" :label="opt.label"
                :model-value="(modelValue || []).includes(opt.value)" hide-details density="compact" color="primary"
                class="mt-0" @update:model-value="toggleCheckboxGroup(opt.value, $event)" />
        </div>
    </div>

    <!-- ── fallback: text ───────────────────────────────────────────────────── -->
    <v-text-field v-else :model-value="modelValue" :label="campo.etichetta" :rules="rules" hide-details="auto"
        density="compact" variant="outlined" @update:model-value="$emit('update:modelValue', $event)" />
</template>

<script>
import { computed } from 'vue'

// Mappa CampoKey → icona MDI
const ICONE_CAMPO = {
    NumeroCommessa: 'mdi-pound',
    Data: 'mdi-calendar-today-outline',
    DataConsegna: 'mdi-calendar-check-outline',
    ViaCliente: 'mdi-map-marker-outline',
    ViaCantiere: 'mdi-map-marker-outline',
    EmailCliente: 'mdi-email-outline',
    TelefonoCliente: 'mdi-phone-outline',
    PiCfCliente: 'mdi-card-text-outline',
}

// Mappa CampoKey → chiave nelle opzioni parametri
const CAMPO_OPZIONI_KEY = {
    Essenza: 'essenze',
    Serie: 'serie',
    Profilo: 'profili',
    Spessore: 'spessori',
    Verniciatura: 'verniciature',
    Persiana: 'persiane',
    VerniciaturaPersiana: 'verniciature',
    Avvolgibili: 'avvolgibili',
    Antone: 'antoine',
    ZanzarieraTipo: 'zanzariereTipo',
    ZanzarieraColore: 'zanzariereColore',
    TipoCommessa: 'tipi',
}

export default {
    name: 'DynamicField',

    props: {
        campo: { type: Object, required: true },
        modelValue: { default: null },
        opzioni: { type: Object, default: () => ({}) },
        rules: { type: Array, default: () => [] }
    },

    emits: ['update:modelValue'],

    setup(props, { emit }) {

        const isTextField = computed(() =>
            ['text', 'number', 'date'].includes(props.campo.tipoInput)
        )

        const inputType = computed(() => {
            if (props.campo.tipoInput === 'number') return 'number'
            if (props.campo.tipoInput === 'date') return 'date'
            return 'text'
        })

        const iconaCampo = computed(() => ICONE_CAMPO[props.campo.campoKey] || null)

        // Opzioni per select/autocomplete/radio/checkboxgroup
        const opzioniCampo = computed(() => {
            const key = CAMPO_OPZIONI_KEY[props.campo.campoKey]
            if (key && props.opzioni[key]) return props.opzioni[key]
            return []
        })

        // Toggle per checkboxgroup (valore array)
        const toggleCheckboxGroup = (value, checked) => {
            const current = Array.isArray(props.modelValue) ? [...props.modelValue] : []
            if (checked) {
                if (!current.includes(value)) current.push(value)
            } else {
                const idx = current.indexOf(value)
                if (idx > -1) current.splice(idx, 1)
            }
            emit('update:modelValue', current)
        }

        return { isTextField, inputType, iconaCampo, opzioniCampo, toggleCheckboxGroup }
    }
}
</script>